import React from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const InvestmentTips = () => {
  const tips = [
    {
      title: 'Validating High-Yield Nodes Beyond Speculative Hype',
      excerpt: 'Evaluating actual physical occupancy statistics and rental density footprints in Kampala without relying on traditional broker hearsay.',
      category: 'Strategic Sourcing',
      readTime: '5 min read'
    },
    {
      title: 'Escrow Lockouts vs. Upfront Capital Risk Outlays',
      excerpt: 'How maintaining absolute control over transaction clearances protects your portfolio growth from casual street fraud mechanics.',
      category: 'Risk Infrastructure',
      readTime: '7 min read'
    },
    {
      title: 'The Pre-Acquisition Land Title Integrity Protocol',
      excerpt: 'A clean operational checklist for searching Mailo, Leasehold, and Freehold titles prior to dispatching micro-listing budgets.',
      category: 'Due Diligence',
      readTime: '6 min read'
    },
    {
      title: 'Tax Deductions on 10,000 UGX Fixed-Broadcast Listings',
      excerpt: 'Maximizing long-term asset visibility overhead margins within localized compliance and rental revenue reporting limits.',
      category: 'Fiscal Parameters',
      readTime: '8 min read'
    },
    {
      title: 'Insulating Diaspora Portfolios via Decentralized Tracking',
      excerpt: 'Eliminating the structural reliance on family proxies by utilizing verified regional zone brokers and milestone verification logs.',
      category: 'Portfolio Scaling',
      readTime: '9 min read'
    },
    {
      title: 'Calibrating Entry Points and Value Cycle Variations',
      excerpt: 'Tracking macro real estate trends and shifting infrastructure corridors across key East African expanding municipal territories.',
      category: 'Ecosystem Timing',
      readTime: '6 min read'
    }
  ];

  return (
    <div className="min-h-screen bg-white text-neutral-900 selection:bg-red-50 selection:text-red-900">
      <Header />
      
      <main className="pt-24">
        
        {/* SECTION 1: HERO (Pristine White Background) */}
        <section className="bg-white tracking-tight">
          <div className="max-w-6xl mx-auto px-6 pt-16 pb-20">
            <div className="mb-8 opacity-70">
              <Breadcrumbs />
            </div>
            
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-4">
                Ecosystem Operational Playbooks
              </span>
              <h1 className="text-4xl sm:text-6xl font-light text-neutral-950 leading-[1.1] mb-6">
                Strategic allocation and <span className="font-serif italic text-neutral-500 block sm:inline">risk insulation.</span>
              </h1>
              <p className="text-lg text-neutral-500 font-light leading-relaxed max-w-xl">
                Deploy capital safely using strict pre-verified milestones, uncompromised due diligence frameworks, and modern real estate escrow protocols.
              </p>
            </div>
          </div>
        </section>


        {/* SECTION 2: INTENTIONAL CHALK GREY RECTANGULAR MASTERCLASS BLOCK */}
        <section className="bg-neutral-50 border-y border-neutral-200/60 py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="bg-white border border-neutral-200/60 p-8 sm:p-12 shadow-sm grid lg:grid-cols-12 gap-8 items-center">
              
              <div className="lg:col-span-8 space-y-4">
                <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-widest font-mono">
                  <span className="bg-red-600 text-white px-2 py-0.5 font-medium">
                    Core Blueprint
                  </span>
                  <span className="text-neutral-400">12 min deployment read</span>
                  <span className="text-neutral-400">Updated May 2026</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-light text-neutral-950 tracking-tight">
                  The Sovereign Real Estate Architecture: Complete Portfolio Framework
                </h2>
                <p className="text-sm text-neutral-500 font-light leading-relaxed max-w-2xl">
                  A comprehensive deep dive designed to isolate your capital allocations from predatory brokerage overheads. Discover how to independently enforce title screening loops and integrate centralized escrow verification models natively.
                </p>
              </div>
              
              <div className="lg:col-span-4 lg:text-right">
                <button className="w-full lg:w-auto inline-flex items-center justify-center px-6 py-3.5 bg-neutral-950 text-white text-sm font-medium tracking-tight hover:bg-neutral-900 transition-colors duration-150 rounded-none">
                  Open Master Blueprint
                </button>
              </div>

            </div>
          </div>
        </section>


        {/* SECTION 3: TAB NAVIGATION & STRUCTURAL GRID (Pristine White Background) */}
        <section className="bg-white py-24">
          <div className="max-w-6xl mx-auto px-6">
            
            {/* Rigid Typographical Architecture Selectors */}
            <div className="flex flex-wrap gap-2 mb-12 justify-center border-b border-neutral-100 pb-6">
              {['All Strategy Matrices', 'Strategic Sourcing', 'Risk Infrastructure', 'Due Diligence', 'Fiscal Parameters', 'Portfolio Scaling'].map((category, idx) => (
                <button
                  key={idx}
                  className={`px-4 py-2 text-xs uppercase tracking-wider transition-all rounded-none border ${
                    idx === 0
                      ? 'bg-neutral-950 border-neutral-950 text-white font-medium'
                      : 'bg-white border-neutral-200 text-neutral-500 hover:text-neutral-950 hover:border-neutral-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Geometric Grid Execution */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {tips.map((tip, index) => (
                <div 
                  key={index} 
                  className="bg-white border border-neutral-200/40 p-6 flex flex-col justify-between hover:border-neutral-300 transition-all shadow-sm group"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-mono">
                      <span className="text-red-600 font-medium">{tip.category}</span>
                      <span className="text-neutral-400">{tip.readTime}</span>
                    </div>
                    <h3 className="text-base font-medium text-neutral-950 tracking-tight group-hover:text-red-600 transition-colors cursor-pointer">
                      {tip.title}
                    </h3>
                    <p className="text-sm text-neutral-500 font-light leading-relaxed">
                      {tip.excerpt}
                    </p>
                  </div>
                  <div className="pt-6">
                    <button className="text-xs uppercase tracking-widest font-medium text-neutral-950 group-hover:text-red-600 flex items-center gap-1 transition-colors">
                      Access Vector <span className="font-mono text-sm">→</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* INTEGRATED RECTANGULAR METRIC TOOL BLOCK */}
            <div className="border border-neutral-200 bg-neutral-50 p-8 sm:p-12 text-center max-w-4xl mx-auto shadow-sm">
              <span className="text-[10px] uppercase tracking-widest font-mono text-neutral-400 block mb-2">
                Simulated Architecture Interface
              </span>
              <h3 className="text-2xl font-light text-neutral-950 tracking-tight mb-3">
                Calibrate Return Projections via Escrow Yield Calculators
              </h3>
              <p className="text-sm text-neutral-500 font-light max-w-xl mx-auto leading-relaxed mb-6">
                Parse asset acquisition figures, flat-broadcast fee balances, and physical zone density data models to instantly project baseline performance thresholds.
              </p>
              <button className="px-6 py-3 bg-neutral-950 text-white text-xs font-medium uppercase tracking-wider hover:bg-neutral-900 transition-colors rounded-none">
                Initialize Computational Engine
              </button>
            </div>

          </div>
        </section>


        {/* SECTION 4: INTELLIGENCE LEDGER REGISTRY (Warm Muted Grey Background) */}
        <section className="bg-neutral-100/70 py-24 border-t border-neutral-200/50">
          <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
            <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block">
              Vector Stream Sync
            </span>
            <h2 className="text-3xl font-light text-neutral-950 tracking-tight">
              Synchronize with absolute market reality.
            </h2>
            <p className="text-sm text-neutral-500 font-light max-w-xl mx-auto leading-relaxed">
              Join institutional partners and verified portfolio operators receiving real-time infrastructural risk alterations, land registry status changes, and platform audit updates.
            </p>
            
            <div className="pt-4 max-w-md mx-auto">
              <form onSubmit={(e) => e.preventDefault()} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder="Enter cryptographic routing email"
                  required
                  className="flex-1 bg-white px-4 py-3 text-sm border border-neutral-200 text-neutral-900 rounded-none focus:outline-none focus:border-neutral-950 transition-colors shadow-sm placeholder:text-neutral-300"
                />
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-red-600 text-white text-sm font-medium tracking-tight hover:bg-red-700 transition-colors duration-150 rounded-none whitespace-nowrap"
                >
                  Bind Account
                </button>
              </form>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default InvestmentTips;